import { ConfigV1 } from './config';
import { Resource } from './resource';
import { Filter } from './filters';
import { Sort, SortDirection, SortColumn } from './sort';
import { Sensor, Capture, CaptureStatus } from './api';

export enum ConfigStatus {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
  MODIFIED = 'MODIFIED',
  ERROR = 'ERROR',
}

export enum ConfigType {
  TRIGGER_LINE_TOP = 'TRIGGER_LINE_TOP',
  TRIGGER_LINE_BOTTOM = 'TRIGGER_LINE_BOTTOM',
  SINGULARITY_TOP = 'SINGULARITY_TOP',
  SINGULARITY_BOTTOM = 'SINGULARITY_BOTTOM',
  SINGULARITY_WALL_RIGHT = 'SINGULARITY_WALL_RIGHT',
  SINGULARITY_WALL_LEFT = 'SINGULARITY_WALL_LEFT',
  SINGULARITY_POLYGONAL = 'SINGULARITY_POLYGONAL',
  RAW = 'RAW',
}

export enum ConfigAlgorithmType {
  REGULAR = 'REGULAR',
  HDR = 'HDR',
}

// Define a number of types to represent representations of kind of config that matches the
// datatypes of each form input.
type WorkingConfigTriggerLine = {
  type: ConfigType.TRIGGER_LINE_TOP | ConfigType.TRIGGER_LINE_BOTTOM;
  maxPixelIntensity: string;

  triggerLineX0: string;
  triggerLineX1: string;
  triggerLineY0: string;
  triggerLineY1: string;

  boundsLinked: boolean;
  upperBound: string;
  lowerBound: string;

  // Store a copy of the original config
  // This is used so any extra parameters can be merged back in later
  rawConfigOverlay: Partial<ConfigV1>;

  // Indicates if the working config has been modified in the form and if the "reset" button
  // should be enabled.
  clean: boolean;

  // Is the sensor running the regular or HDR algorithm?
  algorithm: ConfigAlgorithmType;
};
type WorkingConfigSingularity = {
  type:
    | ConfigType.SINGULARITY_TOP
    | ConfigType.SINGULARITY_BOTTOM
    | ConfigType.SINGULARITY_WALL_RIGHT
    | ConfigType.SINGULARITY_WALL_LEFT
    | ConfigType.SINGULARITY_POLYGONAL;
  maxPixelIntensity: string;
  minimumPathLength: string;
  region: Array<[number, number]>;
  senseiExpression: string;

  // Store a copy of the original config
  // This is used so any extra parameters can be merged back in later
  rawConfigOverlay: Partial<ConfigV1>;

  clean: boolean;

  // Is the sensor running the regular or HDR algorithm?
  algorithm: ConfigAlgorithmType;
};
type WorkingConfigRaw = {
  type: ConfigType.RAW;
  data: string;
  clean: boolean;
};

export type WorkingConfig =
  | WorkingConfigTriggerLine
  | WorkingConfigSingularity
  | WorkingConfigRaw;

export const DEFAULT_PIXEL_INTENSITY = 255;

// Given a sensor config, return all the extra keys in the config that are not modified by the
// sensor configuration form. These are merged with the working config later on when it's converted
// back into a sensor config.
function generateRawConfigOverlay(config: ConfigV1): Partial<ConfigV1> {
  const rawConfigOverlay = { ...config };
  if (rawConfigOverlay.filter) {
    rawConfigOverlay.filter = { ...rawConfigOverlay.filter };
    delete rawConfigOverlay.filter.max_pixel_intensity_override;
    if (Object.keys(rawConfigOverlay.filter).length === 0) {
      delete rawConfigOverlay.filter;
    }
  }
  if (rawConfigOverlay.singularity) {
    rawConfigOverlay.singularity = { ...rawConfigOverlay.singularity };
    delete rawConfigOverlay.singularity.region;
    delete rawConfigOverlay.singularity.sensei_expression;
    delete rawConfigOverlay.singularity.min_path_length;
    if (Object.keys(rawConfigOverlay.singularity).length === 0) {
      delete rawConfigOverlay.singularity;
    }
  }
  if (rawConfigOverlay.trigger_line) {
    rawConfigOverlay.trigger_line = { ...rawConfigOverlay.trigger_line };
    delete rawConfigOverlay.trigger_line.x0_position_override;
    delete rawConfigOverlay.trigger_line.x1_position_override;
    delete rawConfigOverlay.trigger_line.y0_position_override;
    delete rawConfigOverlay.trigger_line.y1_position_override;
    delete rawConfigOverlay.trigger_line.upper_bound_override;
    delete rawConfigOverlay.trigger_line.lower_bound_override;
    if (Object.keys(rawConfigOverlay.trigger_line).length === 0) {
      delete rawConfigOverlay.trigger_line;
    }
  }
  delete rawConfigOverlay.force_non_hdr_model;
  return rawConfigOverlay;
}

// Parse the "force_non_hdr_model" value in the sensor config.
function parseForceNonHDRModelField(
  forceNonHdrModelValue: boolean | undefined,
): ConfigAlgorithmType {
  if (typeof forceNonHdrModelValue === 'boolean') {
    return forceNonHdrModelValue
      ? ConfigAlgorithmType.REGULAR
      : ConfigAlgorithmType.HDR;
  } else {
    return ConfigAlgorithmType.REGULAR;
  }
}

export const WorkingConfig = {
  // Convert from the working config format to the format the sensor uses
  toSensorFormat(config: WorkingConfig): {
    type: ConfigType;
    config: ConfigV1;
  } {
    const parse = (key: string, value: string) => {
      const result = parseInt(value, 10);
      if (isNaN(result)) {
        throw new Error(
          `Error parsing ${key} (value of "${value}") as integer`,
        );
      }
      return result;
    };

    const bound = (n: number) => {
      if (n < 0) {
        return 0;
      }
      return Math.floor(n);
    };
    const boundX = (x: number) => {
      if (x > 160) {
        return 160;
      }
      return bound(x);
    };
    const boundY = (y: number) => {
      if (y > 120) {
        return 120;
      }
      return bound(y);
    };
    const boundMaxPixelIntensity = (n: number) => {
      if (n > 255) {
        return 255;
      } else if (n < 0) {
        return 0;
      }
      return bound(n);
    };

    switch (config.type) {
      case ConfigType.TRIGGER_LINE_TOP:
      case ConfigType.TRIGGER_LINE_BOTTOM:
        const upperBound = bound(parse('Upper Bound', config.upperBound));
        const lowerBound = bound(parse('Lower Bound', config.lowerBound));

        if (config.boundsLinked && upperBound !== lowerBound) {
          throw new Error(
            `Sensor working config upper and lower bound are linked, but are not equal (upper is ${upperBound}, lower is ${lowerBound})`,
          );
        }

        return {
          type: config.type,
          config: {
            ...config.rawConfigOverlay,
            filter: {
              ...config.rawConfigOverlay.filter,
              max_pixel_intensity_override: boundMaxPixelIntensity(
                parse('Pixel Intensity', config.maxPixelIntensity),
              ),
            },
            trigger_line: {
              ...config.rawConfigOverlay.trigger_line,
              x0_position_override: boundX(
                parse('Trigger Line X0', config.triggerLineX0),
              ),
              x1_position_override: boundX(
                parse('Trigger Line X1', config.triggerLineX1),
              ),
              y0_position_override: boundY(
                parse('Trigger Line Y0', config.triggerLineY0),
              ),
              y1_position_override: boundY(
                parse('Trigger Line Y1', config.triggerLineY1),
              ),
              upper_bound_override: upperBound,
              lower_bound_override: lowerBound,
            },
            force_non_hdr_model:
              config.algorithm === ConfigAlgorithmType.REGULAR,
          },
        };

      case ConfigType.SINGULARITY_TOP:
      case ConfigType.SINGULARITY_BOTTOM:
      case ConfigType.SINGULARITY_WALL_RIGHT:
      case ConfigType.SINGULARITY_WALL_LEFT:
      case ConfigType.SINGULARITY_POLYGONAL:
        const minimumPathLength = parse(
          'Minimum Path Length',
          config.minimumPathLength,
        );
        if (minimumPathLength < 0) {
          throw new Error(
            'Minimum path length must be greater than or equal to zero.',
          );
        }
        return {
          type: config.type,
          config: {
            ...config.rawConfigOverlay,
            filter: {
              ...config.rawConfigOverlay.filter,
              max_pixel_intensity_override: boundMaxPixelIntensity(
                parse('Pixel Intensity', config.maxPixelIntensity),
              ),
            },
            singularity: {
              ...config.rawConfigOverlay.singularity,
              sensei_expression: config.senseiExpression,
              region: config.region.map(([x, y]) => [boundX(x), boundY(y)]),
              min_path_length: minimumPathLength,
            },
            force_non_hdr_model:
              config.algorithm === ConfigAlgorithmType.REGULAR,
          },
        };

      case ConfigType.RAW: {
        let parsed;
        try {
          parsed = JSON.parse(config.data);
        } catch (e) {
          throw new Error(`Error parsing raw working config: ${e}`);
        }

        // FIXME: This is a hueristic. I'm not sure how well this will work long term.
        let configType;
        if (parsed.trigger_line) {
          configType = ConfigType.TRIGGER_LINE_BOTTOM;
        } else if (parsed.singularity) {
          configType = ConfigType.SINGULARITY_POLYGONAL;
        } else {
          throw new Error(
            'Unable to determine if raw sensor config contains a trigger line or singularity region!',
          );
        }

        return {
          type: configType,
          config: parsed,
        };
      }
    }
  },

  // Construct a new working config from the config format the sensor uses
  fromSensorFormat(configType: ConfigType, config: ConfigV1): WorkingConfig {
    // Store all the extra config keys that the sensor configuration interface doesn't modify.
    // Storing these allows them to be included in any the sensor configurations generated from
    // this working configuration.
    const rawConfigOverlay = generateRawConfigOverlay(config);

    switch (configType) {
      case ConfigType.TRIGGER_LINE_TOP:
      case ConfigType.TRIGGER_LINE_BOTTOM:
        return {
          type: configType,
          maxPixelIntensity: `${config.filter.max_pixel_intensity_override}`,

          triggerLineX0: `${config.trigger_line.x0_position_override}`,
          triggerLineX1: `${config.trigger_line.x1_position_override}`,
          triggerLineY0: `${config.trigger_line.y0_position_override}`,
          triggerLineY1: `${config.trigger_line.y1_position_override}`,

          boundsLinked:
            config.trigger_line.upper_bound_override ===
            config.trigger_line.lower_bound_override,
          upperBound: `${config.trigger_line.upper_bound_override}`,
          lowerBound: `${config.trigger_line.lower_bound_override}`,

          rawConfigOverlay,
          clean: true,
          algorithm: parseForceNonHDRModelField(config.force_non_hdr_model),
        };

      case ConfigType.SINGULARITY_TOP:
      case ConfigType.SINGULARITY_BOTTOM:
      case ConfigType.SINGULARITY_WALL_RIGHT:
      case ConfigType.SINGULARITY_WALL_LEFT:
      case ConfigType.SINGULARITY_POLYGONAL:
        return {
          type: configType,
          maxPixelIntensity: `${config.filter.max_pixel_intensity_override}`,
          minimumPathLength: `${config.singularity.min_path_length}`,
          senseiExpression: config.singularity.sensei_expression,
          region: config.singularity.region,
          rawConfigOverlay,
          clean: true,
          algorithm: parseForceNonHDRModelField(config.force_non_hdr_model),
        };

      default:
        return {
          type: ConfigType.RAW,
          data: JSON.stringify(config, null, 2),
          clean: true,
        };
    }
  },

  // Migrate a config from one type to another
  migrateConfigType(config: WorkingConfig, toType: ConfigType): WorkingConfig {
    // Migrating to the same config type as the config already is should do nothing.
    if (config.type === toType) {
      return config;
    }

    const maxPixelIntensity =
      config.type === ConfigType.RAW
        ? `${DEFAULT_PIXEL_INTENSITY}`
        : config.maxPixelIntensity;

    const rawConfigOverlay =
      config.type === ConfigType.RAW
        ? generateRawConfigOverlay(JSON.parse(config.data))
        : config.rawConfigOverlay;

    // If migrating from a raw config, try to look for a "force_non_hdr_model" key.
    // Use this to determine if the algorithm is new.
    const algorithm =
      config.type === ConfigType.RAW
        ? parseForceNonHDRModelField(
            JSON.parse(config.data).force_non_hdr_model,
          )
        : config.algorithm;

    switch (toType) {
      case ConfigType.TRIGGER_LINE_BOTTOM:
        // Also change the configuration in
        // https://github.com/DensityCo/core-api/blob/trunk/core_api/api/models/sensor_model.py#L72-L88
        // if changing these
        return {
          type: ConfigType.TRIGGER_LINE_BOTTOM,
          maxPixelIntensity,

          triggerLineX0: '0',
          triggerLineX1: '160',
          triggerLineY0: '80',
          triggerLineY1: '80',

          boundsLinked: true,
          lowerBound: '10',
          upperBound: '10',

          rawConfigOverlay,
          clean: false,
          algorithm,
        };

      case ConfigType.SINGULARITY_BOTTOM:
        return {
          type: ConfigType.SINGULARITY_BOTTOM,
          maxPixelIntensity,
          senseiExpression: '[3, [160, 95], [80, 36], [0, 95]]',
          minimumPathLength: '20',
          region: [
            [0, 120],
            [0, 95],
            [15.99, 84.38],
            [31.99, 76.12],
            [48.0, 70.22],
            [64, 66.68],
            [80, 65.5],
            [96, 66.68],
            [112, 70.22],
            [128.0, 76.12],
            [144, 84.38],
            [160, 95],
            [160, 120],
          ],

          rawConfigOverlay,
          clean: false,
          algorithm,
        };

      case ConfigType.SINGULARITY_POLYGONAL:
        return {
          type: ConfigType.SINGULARITY_POLYGONAL,
          maxPixelIntensity,
          minimumPathLength: '20',
          senseiExpression: '',
          region: [
            [160, 120],
            [150, 99],
            [134, 81],
            [115, 68],
            [92, 61],
            [68, 61],
            [45, 68],
            [26, 81],
            [10, 99],
            [0, 120],
          ],

          rawConfigOverlay,
          clean: false,
          algorithm,
        };

      case ConfigType.SINGULARITY_WALL_LEFT:
        return {
          type: ConfigType.SINGULARITY_WALL_LEFT,
          maxPixelIntensity,
          senseiExpression: '[5, [160, 120], [140, 68], [77, 66]]',
          minimumPathLength: '20',
          region: [
            [0, 66],
            [77, 66],
            [89, 67],
            [100, 69],
            [111, 71],
            [121, 75],
            [129, 80],
            [137, 85],
            [144, 92],
            [150, 99],
            [156, 108],
            [160, 120],
            [0, 120],
          ],

          rawConfigOverlay,
          clean: false,
          algorithm,
        };

      case ConfigType.SINGULARITY_WALL_RIGHT:
        return {
          type: ConfigType.SINGULARITY_WALL_RIGHT,
          maxPixelIntensity,
          senseiExpression: '[4, [0, 120], [20, 68], [83, 66]]',
          minimumPathLength: '20',
          region: [
            [160, 66],
            [83, 66],
            [71, 67],
            [60, 69],
            [49, 71],
            [39, 75],
            [31, 80],
            [23, 85],
            [16, 92],
            [10, 99],
            [4, 108],
            [0, 120],
            [160, 120],
          ],

          rawConfigOverlay,
          clean: false,
          algorithm,
        };

      case ConfigType.RAW:
        return {
          type: ConfigType.RAW,
          data: JSON.stringify(
            WorkingConfig.toSensorFormat(config).config,
            null,
            2,
          ),
          clean: false,
        };
    }
  },
};

export type ConfigStateConfigDetails = {
  sensor: Sensor;
  status: string;

  in_mpi: boolean;

  workingConfig: WorkingConfig;
  workingConfigValidationError: Error | null;

  mutable_config: ConfigV1;
  mutable_config_template: ConfigType;
  assigned_config: ConfigV1;
  assigned_config_template: ConfigType;
  config_status: ConfigStatus;

  initialAccuracyPercentage: number | null;
};

export type ConfigStateCaptureDetails = {
  id: number;
  tags: Array<{
    id: number;
    name: string;
    created_at: string;
    start_frame: number;
  }>;
  latest_report: {
    id: number;
    report_name: string;
    completed_at: string | null;
    egress_accuracy: number | null;
    ingress_accuracy: number | null;
    total_accuracy: number | null;
    algo_events: Array<{
      id: any;
      seconds_offset: string;
      direction: 1 | -1;
      x_position: number;
      y_position: number;
      is_dupe: boolean;
    }>;
    annotated_events: Array<{
      id: any;
      seconds_offset: string;
      direction: 1 | -1;
      x_position: number;
      y_position: number;
    }>;
  } | null;
  annotation_status: CaptureStatus;
  created_at: string;

  capture_url: string;
  inferences: any;
};

// On this page, captures can optionally be briefly highlghted to show that
// they are newly received from a sensor.
export type HighlightableCapture = Capture & {
  highlighted: boolean;
};

export type ConfigState = {
  config: Resource<ConfigStateConfigDetails>;
  selectedCapture: Resource<ConfigStateCaptureDetails>;

  captures: {
    searchText: string;
    filters: Array<Filter>;
    sort: Sort;
    initialStartTimeSeconds?: number;
    selectedId: HighlightableCapture['id'];
    resource: Resource<Array<HighlightableCapture>>;
  };

  reports: {
    capturesBeingRun: Array<Capture['id']>;
    results: {
      [captureId: number]: {
        totalAccuracy: number;
      };
    };
  };
};

export enum SensorConfigActionTypes {
  SENSOR_CONFIG_CHANGE_PAGE = 'SENSOR_CONFIG_CHANGE_PAGE',
  SENSOR_CONFIG_LEAVE_PAGE = 'SENSOR_CONFIG_LEAVE_PAGE',

  SENSOR_CONFIG_LOAD_LOADING = 'SENSOR_CONFIG_LOAD_LOADING',
  SENSOR_CONFIG_LOAD_ERROR = 'SENSOR_CONFIG_LOAD_ERROR',
  SENSOR_CONFIG_LOAD_COMPLETE = 'SENSOR_CONFIG_LOAD_COMPLETE',

  SENSOR_CONFIG_CHANGE_TYPE = 'SENSOR_CONFIG_CHANGE_TYPE',
  SENSOR_CONFIG_UPDATE_DATA = 'SENSOR_CONFIG_UPDATE_DATA',
  SENSOR_CONFIG_RESET_CONFIGURATION = 'SENSOR_CONFIG_RESET_CONFIGURATION',

  SENSOR_CONFIG_CHANGE_MAX_PIXEL_INTENSITY = 'SENSOR_CONFIG_CHANGE_MAX_PIXEL_INTENSITY',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X0 = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X0',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y0 = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y0',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X1 = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X1',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y1 = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y1',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_UPPER_BOUND = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_UPPER_BOUND',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_LOWER_BOUND = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_LOWER_BOUND',
  SENSOR_CONFIG_CHANGE_TRIGGER_LINE_BOUNDS_LINKED = 'SENSOR_CONFIG_CHANGE_TRIGGER_LINE_BOUNDS_LINKED',
  SENSOR_CONFIG_CHANGE_SINGULARITY_REGION = 'SENSOR_CONFIG_CHANGE_SINGULARITY_REGION',
  SENSOR_CONFIG_CHANGE_MINIMUM_PATH_LENGTH = 'SENSOR_CONFIG_CHANGE_MIN_PATH_LENGTH',
  SENSOR_CONFIG_CHANGE_ALGORITHM = 'SENSOR_CONFIG_CHANGE_ALGORITHM',
  SENSOR_CONFIG_CHANGE_RAW_CONFIG = 'SENSOR_CONFIG_CHANGE_RAW_CONFIG',

  SENSOR_CONFIG_ADD_REPORT_LOADING = 'SENSOR_CONFIG_ADD_REPORT_LOADING',
  SENSOR_CONFIG_SET_REPORT_COMPLETE = 'SENSOR_CONFIG_SET_REPORT_COMPLETE',
  SENSOR_CONFIG_SET_REPORT_ERROR = 'SENSOR_CONFIG_SET_REPORT_ERROR',
  SENSOR_CONFIG_RESET_REPORT = 'SENSOR_CONFIG_RESET_REPORT',

  SENSOR_CONFIG_CAPTURES_LOAD_START = 'SENSOR_CONFIG_CAPTURES_LOAD_START',
  SENSOR_CONFIG_CAPTURES_LOAD_ERROR = 'SENSOR_CONFIG_CAPTURES_LOAD_ERROR',
  SENSOR_CONFIG_CAPTURES_LOAD_COMPLETE = 'SENSOR_CONFIG_CAPTURES_LOAD_COMPLETE',
  SENSOR_CONFIG_CAPTURES_PUSH_NEW = 'SENSOR_CONFIG_CAPTURES_PUSH_NEW',
  SENSOR_CONFIG_CAPTURES_CLEAR_HIGHLIGHT = 'SENSOR_CONFIG_CAPTURES_CLEAR_HIGHLIGHT',
  SENSOR_CONFIG_CAPTURES_SET_FILTERS = 'SENSOR_CONFIG_CAPTURES_SET_FILTERS',
  SENSOR_CONFIG_CAPTURES_SET_SORT = 'SENSOR_CONFIG_CAPTURES_SET_SORT',
  SENSOR_CONFIG_CAPTURES_SET_SEARCH = 'SENSOR_CONFIG_CAPTURES_SET_SEARCH',
  SENSOR_CONFIG_CAPTURES_SET_SELECTED = 'SENSOR_CONFIG_CAPTURES_SET_SELECTED',

  SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_START = 'SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_START',
  SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_ERROR = 'SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_ERROR',
  SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_COMPLETE = 'SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_COMPLETE',

  SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK = 'SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK',
}

export type SensorConfigAction =
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_PAGE;
      sensorSerialNumber: number;
      params: {
        capture_id?: number;
        t?: number;
      };
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_LEAVE_PAGE;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_LOAD_LOADING;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_LOAD_ERROR;
      error: any;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_LOAD_COMPLETE;
      sensor: Sensor;

      in_mpi: boolean;

      assigned_config: ConfigV1;
      assigned_config_template: ConfigType;
      mutable_config: ConfigV1;
      mutable_config_template: ConfigType;

      config_status: ConfigStatus;

      initialAccuracyPercentage: number;
      initialStartTimeSeconds?: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_PUSH_NEW;
      captures: Array<Capture>;
    }
  | { type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_CLEAR_HIGHLIGHT }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TYPE;
      configType: ConfigType;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_UPDATE_DATA;
      data: ConfigV1;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_MAX_PIXEL_INTENSITY;
      maxPixelIntensity: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE;
      x0: number;
      x1: number;
      y0: number;
      y1: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X0;
      x0: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_X1;
      x1: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y0;
      y0: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_Y1;
      y1: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_UPPER_BOUND;
      upperBound: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_LOWER_BOUND;
      lowerBound: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_TRIGGER_LINE_BOUNDS_LINKED;
      linked: boolean;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_SINGULARITY_REGION;
      region: Array<[number, number]>;
      senseiExpression: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_MINIMUM_PATH_LENGTH;
      minimumPathLength: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_RAW_CONFIG;
      rawConfig: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CHANGE_ALGORITHM;
      algorithm: ConfigAlgorithmType;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_RESET_CONFIGURATION;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_FILTERS;
      filters: Array<Filter>;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_ADD_REPORT_LOADING;
      capture_id: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_SET_REPORT_COMPLETE;
      capture_id: number;
      accuracy: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_SET_REPORT_ERROR;
      capture_id: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_RESET_REPORT;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_START;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_ERROR;
      error: any;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_LOAD_COMPLETE;
      captures: Array<Capture>;
      sensorSerialNumber: Sensor['serial_number'];
      initialSelectedCaptureId?: Capture['id'];
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_FILTERS;
      filters: Array<Filter>;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SORT;
      direction: SortDirection;
      column: SortColumn;
      captures: Array<HighlightableCapture>;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SEARCH;
      search: string;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_CAPTURES_SET_SELECTED;
      selectedId: number;
      startTime?: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_VIEWER_SEEK;
      seek: number;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_START;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_ERROR;
      error: any;
    }
  | {
      type: SensorConfigActionTypes.SENSOR_CONFIG_SELECTED_CAPTURE_LOAD_COMPLETE;
      selectedCapture: ConfigStateCaptureDetails;
    };
